/**
 * Attendance calculation engine: policies, shifts, punches → PR / LOP / LOP/PR / PR/LOP.
 * Policies are persisted via /api/attendance-structure/attendance-policies and mirrored
 * into localStorage so punch evaluation stays sync/offline-capable.
 */

import {
  getEffectiveTimingConfig,
  HALF_DAY_LOP_PR,
  HALF_DAY_PR_LOP,
  isPolicyOverrideAttendance,
  missingPunchMarkToStatus,
  normalizePolicyRecord,
  normalizeShiftRecord,
  penaltyToDayStatus,
} from "./attendancePolicyConfig";
import {
  canConsumeEarlyGrace,
  canConsumeLateGrace,
  recordGraceUsage,
} from "./attendanceGraceLedger";
import { getShiftOverrideForDay, resolveApprovedChangeShiftName } from "./attendanceShiftOverride";
import { getLocalDayKey, workedMsFromSessions } from "./attendancePunchStorage";
import {
  hasApprovedChangeWoForDay,
  hasApprovedProposedWoForDay,
} from "./weeklyOffChange";

export const POLICIES_STORAGE_KEY = "hrms_frontend_attendance_policies_v1";
export const SHIFTS_STORAGE_KEY = "hrms_frontend_attendance_shifts_v1";

const ORG_APPLICABILITY_KEYS = [
  "branch",
  "department",
  "designation",
  "grade",
  "category",
  "subDepartment",
  "location",
];

const STRUCTURAL_STATUS_CODES = new Set([
  "WO",
  "PH",
  "HO",
  "CO",
  "CW",
  "OD",
  "WFH",
  "RH",
]);

export function loadAttendancePolicies() {
  try {
    const raw = localStorage.getItem(POLICIES_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    const list = Array.isArray(parsed) ? parsed : [];
    return list.map(normalizePolicyRecord);
  } catch {
    return [];
  }
}

export function loadAttendanceShifts() {
  try {
    const raw = localStorage.getItem(SHIFTS_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    const list = Array.isArray(parsed) ? parsed : [];
    return list.map(normalizeShiftRecord);
  } catch {
    return [];
  }
}

export function parseIndividualIds(policy) {
  const s = policy?.individualEmployeeIds;
  if (Array.isArray(s)) return s.map(String).filter(Boolean);
  return String(s || "")
    .split(/[\s,]+/)
    .map((x) => x.trim())
    .filter(Boolean);
}

function normalizeEmploymentTypes(policy) {
  const a = policy?.employmentTypes;
  if (!Array.isArray(a) || a.length === 0) return null;
  return [...new Set(a.map(String))];
}

function normalizeEmploymentTypeToken(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
}

function employmentTypesCompatible(policyTypes, employeeType) {
  if (!policyTypes) return true;
  const et = normalizeEmploymentTypeToken(employeeType);
  if (!et) return true;
  return policyTypes.map(normalizeEmploymentTypeToken).includes(et);
}

function employeeOrgField(employee, key) {
  const map = {
    branch: employee?.branchId ?? employee?.branch ?? employee?.branch_id,
    department: employee?.departmentId ?? employee?.department ?? employee?.department_id,
    designation:
      employee?.designationId ?? employee?.designation ?? employee?.designation_id,
    grade: employee?.gradeId ?? employee?.grade ?? employee?.grade_id,
    category: employee?.categoryId ?? employee?.category ?? employee?.category_id,
    subDepartment:
      employee?.subDepartmentId ??
      employee?.subDepartment ??
      employee?.sub_department_id,
    location: employee?.locationId ?? employee?.location ?? employee?.workLocation,
    groupId: employee?.groupId ?? employee?.attendanceGroupId ?? employee?.policyGroupId,
  };
  const v = map[key];
  return v != null && v !== "" ? String(v) : "";
}

function employeeIdentityTokens(employee) {
  return [
    employee?.id,
    employee?.employeeId,
    employee?.code,
    employee?.empCode,
    employee?.employeeCode,
  ]
    .filter((x) => x != null && x !== "")
    .map(String);
}

export function isPolicyEffectiveOnDate(policy, date) {
  if (!policy?.effectiveDate) return true;
  const ed = new Date(policy.effectiveDate);
  if (isNaN(ed.getTime())) return true;
  const d = date instanceof Date ? date : new Date(date);
  return ed <= d;
}

export function employeeMatchesPolicy(employee, policy) {
  if (!employee || !policy || policy.status === "inactive") return false;

  const tokens = new Set(employeeIdentityTokens(employee));
  const individualIds = parseIndividualIds(policy);
  if (individualIds.length > 0) {
    return individualIds.some((id) => tokens.has(String(id)));
  }

  if (policy.allEmployees) {
    const emTypes = normalizeEmploymentTypes(policy);
    if (emTypes) {
      const et = String(
        employee.employmentType || employee.employment_type || ""
      ).trim();
      // Missing employment type on employee: still in "all employees" scope.
      if (!et) return true;
      return employmentTypesCompatible(emTypes, et);
    }
    return true;
  }

  let matchedOrgField = false;
  for (const k of ORG_APPLICABILITY_KEYS) {
    const pv = policy[k] ? String(policy[k]) : "";
    if (!pv) continue;
    matchedOrgField = true;
    const ev = employeeOrgField(employee, k);
    if (ev !== pv) return false;
  }

  const emTypes = normalizeEmploymentTypes(policy);
  if (emTypes) {
    const et = String(
      employee.employmentType || employee.employment_type || ""
    ).trim();
    if (et && !employmentTypesCompatible(emTypes, et)) return false;
  }

  return matchedOrgField || !!emTypes;
}

function policySpecificityScore(policy) {
  const ids = parseIndividualIds(policy);
  if (ids.length > 0) return 1000 + ids.length;
  let score = 0;
  for (const k of ORG_APPLICABILITY_KEYS) {
    if (policy[k]) score += 10;
  }
  const em = normalizeEmploymentTypes(policy);
  if (em) score += 5;
  if (policy.allEmployees) score += 1;
  // Prefer Manual Override over demo/auto punch policies when both match.
  if (isPolicyOverrideAttendance(policy)) score += 50;
  return score;
}

export function resolvePolicyForEmployee(employee, policies, asOfDate = new Date()) {
  const list = Array.isArray(policies) ? policies : [];
  const active = list.filter(
    (p) => p && p.status !== "inactive" && isPolicyEffectiveOnDate(p, asOfDate)
  );
  const matching = active.filter((p) => employeeMatchesPolicy(employee, p));
  if (matching.length === 0) {
    return active.find((p) => p.isDefault) || active[0] || null;
  }
  // Prefer Override Attendance (manual) when several policies match.
  const overrideMatches = matching.filter((p) => isPolicyOverrideAttendance(p));
  const pool = overrideMatches.length > 0 ? overrideMatches : matching;
  return [...pool].sort((a, b) => policySpecificityScore(b) - policySpecificityScore(a))[0];
}

/**
 * Effective attendance process for an employee (policy-driven).
 * Manual when assigned policy has Auto attendance + Override Attendance.
 */
export function resolveEmployeeAttendanceProcess(
  employee,
  asOfDate = new Date(),
  policies = null
) {
  const policyList = policies ?? loadAttendancePolicies();
  const policy = resolvePolicyForEmployee(employee, policyList, asOfDate);
  return isPolicyOverrideAttendance(policy) ? "manual" : "auto";
}

export function getWorkingDayDefaultForEmployee(employee, asOfDate = new Date()) {
  return resolveEmployeeAttendanceProcess(employee, asOfDate) === "manual"
    ? "PR"
    : "LOP";
}

export function isEmployeeManualAttendanceProcess(employee, asOfDate = new Date()) {
  return resolveEmployeeAttendanceProcess(employee, asOfDate) === "manual";
}

/**
 * Policy-driven ESS access for calendar day status edit.
 * Punch-based policies (autoAttendance off): never allowed.
 * Auto attendance: allowed only when employeeStatusEditEnabled on assigned policy.
 */
export function resolveEmployeeStatusEditAccess(
  employee,
  asOfDate = new Date(),
  policies = null
) {
  const policyList = policies ?? loadAttendancePolicies();
  const raw = resolvePolicyForEmployee(employee, policyList, asOfDate);
  const policy = normalizePolicyRecord(raw);
  if (!policy?.autoAttendance) {
    return {
      allowed: false,
      requiresWorkflow: false,
      reason: "punch_based",
      policy,
    };
  }
  if (!policy.employeeStatusEditEnabled) {
    return {
      allowed: false,
      requiresWorkflow: false,
      reason: "policy_disabled",
      policy,
    };
  }
  return {
    allowed: true,
    requiresWorkflow: !!policy.employeeStatusEditRequiresWorkflow,
    reason: "allowed",
    policy,
  };
}

export function resolveShiftForPolicy(policy, shifts, shiftNameHint = null) {
  const list = Array.isArray(shifts) ? shifts : [];
  if (shiftNameHint) {
    const byName = list.find(
      (s) =>
        String(s?.name || "").trim().toLowerCase() ===
        String(shiftNameHint).trim().toLowerCase()
    );
    if (byName) return byName;
  }
  const shiftId = policy?.shiftGroupId;
  if (shiftId) {
    const linked = list.find((s) => String(s.id) === String(shiftId));
    if (linked) return linked;
  }
  return (
    list.find((s) => s?.isDefault) ||
    list.find((s) => String(s?.name || "").trim().toLowerCase() === "general") ||
    list[0] ||
    null
  );
}

/** Resolve shift including per-day override set before check-in. */
export function resolveShiftForEmployeeDay(
  employee,
  policy,
  shifts,
  date = new Date(),
  shiftNameHint = null
) {
  const dayKey = getLocalDayKey(date instanceof Date ? date.getTime() : date);
  const override = getShiftOverrideForDay(employee?.id, dayKey);
  if (override?.shift) return override.shift;
  if (override?.shiftName) {
    const byOverride = resolveShiftForPolicy(policy, shifts, override.shiftName);
    if (byOverride) return byOverride;
  }
  const dayKeyName = getLocalDayKey(date instanceof Date ? date.getTime() : date);
  const approvedName = resolveApprovedChangeShiftName(employee?.id, dayKeyName);
  if (approvedName) {
    const byApproved = resolveShiftForPolicy(policy, shifts, approvedName);
    if (byApproved) return byApproved;
  }
  return resolveShiftForPolicy(policy, shifts, shiftNameHint);
}

export function isFlexibleShift(shift) {
  if (!shift) return false;
  if (shift.shiftType === "flexi" || shift.flexiEnabled) return true;
  const name = String(shift.name || "").toLowerCase();
  const id = String(shift.id || "").toLowerCase();
  return name.includes("flex") || id.includes("flex");
}

export function shiftWindowMinutes(shift) {
  if (!shift) return { start: 9 * 60, end: 18 * 60, overnight: false };
  const start = parseTimeToMinutes(shift.startTime) ?? 9 * 60;
  let end = parseTimeToMinutes(shift.endTime) ?? 18 * 60;
  const overnight = !!(shift.crossDayShift) || end <= start;
  return { start, end, overnight };
}

function flexiLateMinutes(checkInMin, config) {
  // Bandwidth end = last allowed flexi check-in. Core start is the overlap window,
  // not the late clock (so 14:00 inside 13:00–15:00 is not late).
  const lateFrom =
    parseTimeToMinutes(config.flexiBandwidthEnd) ??
    parseTimeToMinutes(config.coreHoursStart);
  if (lateFrom == null || checkInMin == null) return { late: false, lateMinutes: 0 };
  const grace = Number(config.graceCheckInMinutes ?? 15);
  const lateAfter = Number(config.considerLateAfterMinutes ?? 15);
  const allowedLatest = lateFrom + grace + lateAfter;
  const lateMinutes = Math.max(0, checkInMin - (lateFrom + grace));
  return {
    late: checkInMin > allowedLatest,
    lateMinutes,
  };
}

/**
 * Late / early vs shift + merged policy/shift grace; supports grace occurrence budget.
 */
export function evaluateTimingRules(
  policy,
  shift,
  checkIn,
  checkOut,
  { employeeId = null, date = new Date(), consumeGrace = true } = {}
) {
  const config = getEffectiveTimingConfig(policy, shift);
  const reasons = [];

  if (!policy || isFlexibleShift(shift)) {
    const inMin = parseTimeToMinutes(checkIn);
    if (isFlexibleShift(shift) && inMin != null) {
      const flexi = flexiLateMinutes(inMin, config);
      if (flexi.late) {
        return applyLatePenaltyDecision({
          config,
          employeeId,
          date,
          late: true,
          earlyExit: false,
          lateMinutes: flexi.lateMinutes,
          consumeGrace,
          reasons: ["flexi_late"],
        });
      }
    }
    return {
      late: false,
      earlyExit: false,
      lateMinutes: 0,
      forceLop: false,
      penaltyStatus: null,
      graceConsumed: false,
      shortLeaveSuggested: false,
      requiresShiftOverride: false,
      config,
      reasons,
    };
  }

  const { start, end, overnight } = shiftWindowMinutes(shift);
  const inMin = parseTimeToMinutes(checkIn);
  const outMin = parseTimeToMinutes(checkOut);

  const graceIn = config.graceCheckInMinutes;
  const graceOut = config.graceCheckOutMinutes;
  const lateAfter = config.considerLateAfterMinutes;
  const earlyBefore = config.considerEarlyBeforeMinutes;

  let late = false;
  let earlyExit = false;
  let lateMinutes = 0;

  if (inMin != null) {
    const shiftStartWithGrace = start + graceIn;
    lateMinutes = Math.max(0, inMin - shiftStartWithGrace);
    const allowedLatest = start + graceIn + lateAfter;
    late = inMin > allowedLatest;
  }

  if (outMin != null && !overnight) {
    const allowedEarliest = end - graceOut - earlyBefore;
    earlyExit = outMin < allowedEarliest;
  }

  if (late) {
    return applyLatePenaltyDecision({
      config,
      employeeId,
      date,
      late: true,
      earlyExit,
      lateMinutes,
      consumeGrace,
      reasons: ["late_after_shift_grace"],
      shift,
    });
  }

  if (earlyExit) {
    return applyEarlyPenaltyDecision({
      config,
      employeeId,
      date,
      earlyExit: true,
      consumeGrace,
      reasons: ["early_departure"],
    });
  }

  return {
    late: false,
    earlyExit: false,
    lateMinutes: 0,
    forceLop: false,
    penaltyStatus: null,
    graceConsumed: false,
    shortLeaveSuggested: false,
    requiresShiftOverride: false,
    config,
    reasons,
  };
}

function applyLatePenaltyDecision({
  config,
  employeeId,
  date,
  late,
  earlyExit,
  lateMinutes,
  consumeGrace,
  reasons,
  shift,
}) {
  const window = config.graceOccurrenceWindow || "monthly";
  const allowedLate = config.lateGraceAllowedOccurrences;

  if (
    config.graceRulesEnabled &&
    employeeId &&
    canConsumeLateGrace(employeeId, date, allowedLate, window)
  ) {
    if (consumeGrace) recordGraceUsage(employeeId, date, { late: true, windowType: window });
    return {
      late: true,
      earlyExit,
      lateMinutes,
      forceLop: false,
      penaltyStatus: null,
      graceConsumed: true,
      shortLeaveSuggested: lateMinutes <= config.shortLeaveMaxMinutes,
      requiresShiftOverride: false,
      config,
      reasons: [...reasons, "grace_occurrence_applied"],
    };
  }

  const shortLeaveSuggested =
    lateMinutes > 0 &&
    lateMinutes <= config.shortLeaveMaxMinutes &&
    config.regularizationAllowed;

  const requiresShiftOverride =
    config.allowShiftOverride &&
    lateMinutes > config.considerLateAfterMinutes;

  const penaltyStatus = penaltyToDayStatus(config.latePenaltyType, "late");
  const forceLop = penaltyStatus === "LOP";

  return {
    late: true,
    earlyExit,
    lateMinutes,
    forceLop,
    penaltyStatus,
    penaltyType: config.latePenaltyType,
    graceConsumed: false,
    shortLeaveSuggested,
    requiresShiftOverride,
    config,
    reasons: [
      ...reasons,
      forceLop ? "late_penalty" : "late_no_penalty",
      shortLeaveSuggested ? "short_leave_window" : null,
      requiresShiftOverride ? "shift_override_recommended" : null,
    ].filter(Boolean),
  };
}

function applyEarlyPenaltyDecision({
  config,
  employeeId,
  date,
  earlyExit,
  consumeGrace,
  reasons,
}) {
  const window = config.graceOccurrenceWindow || "monthly";
  const allowedEarly = config.earlyGraceAllowedOccurrences;

  if (
    config.graceRulesEnabled &&
    employeeId &&
    canConsumeEarlyGrace(employeeId, date, allowedEarly, window)
  ) {
    if (consumeGrace) recordGraceUsage(employeeId, date, { early: true, windowType: window });
    return {
      late: false,
      earlyExit: true,
      lateMinutes: 0,
      forceLop: false,
      penaltyStatus: null,
      graceConsumed: true,
      shortLeaveSuggested: false,
      requiresShiftOverride: false,
      config,
      reasons: [...reasons, "early_grace_occurrence_applied"],
    };
  }

  const penaltyStatus = penaltyToDayStatus(config.earlyDeparturePenaltyType, "early");
  return {
    late: false,
    earlyExit: true,
    lateMinutes: 0,
    forceLop: penaltyStatus === "LOP",
    penaltyStatus,
    penaltyType: config.earlyDeparturePenaltyType,
    graceConsumed: false,
    shortLeaveSuggested: false,
    requiresShiftOverride: false,
    config,
    reasons: [...reasons, "early_penalty"],
  };
}

export function parseTimeToMinutes(raw) {
  if (raw == null || raw === "" || raw === "--") return null;
  if (typeof raw === "number" && Number.isFinite(raw)) {
    if (raw > 1e11) {
      const d = new Date(raw);
      if (!isNaN(d.getTime())) return d.getHours() * 60 + d.getMinutes();
    }
    return raw;
  }
  const s = String(raw).trim();
  const asNum = Number(s);
  if (Number.isFinite(asNum) && asNum > 1e11) {
    const d = new Date(asNum);
    if (!isNaN(d.getTime())) return d.getHours() * 60 + d.getMinutes();
  }
  // ISO instants must use local wall-clock, not the UTC "T05:05" fragment.
  if (/^\d{4}-\d{2}-\d{2}T/.test(s) || /Z$/i.test(s)) {
    const d = new Date(s);
    if (!isNaN(d.getTime())) return d.getHours() * 60 + d.getMinutes();
  }

  const ampm = s.match(
    /^(\d{1,2}):(\d{2})(?::\d{2})?\s*([AaPp]\.?[Mm]\.?)$/
  );
  if (ampm) {
    let h = parseInt(ampm[1], 10);
    const m = parseInt(ampm[2], 10);
    const ap = ampm[3].replace(/\./g, "").toUpperCase();
    if (ap === "PM" && h < 12) h += 12;
    if (ap === "AM" && h === 12) h = 0;
    return h * 60 + m;
  }
  const hm = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (hm) return parseInt(hm[1], 10) * 60 + parseInt(hm[2], 10);

  const d = new Date(s);
  if (!isNaN(d.getTime()) && s.includes("-")) {
    return d.getHours() * 60 + d.getMinutes();
  }
  return null;
}

export function computeWorkedHours(checkIn, checkOut, policy = {}, shift = null, options = {}) {
  const {
    treatOpenCheckoutAsNow = false,
    deductBreak = !treatOpenCheckoutAsNow,
    inMs = null,
    outMs = null,
    nowMs = Date.now(),
  } = options;
  const config = getEffectiveTimingConfig(policy, shift);

  let diffMin = null;
  const startMs = Number.isFinite(Number(inMs)) && Number(inMs) > 0 ? Number(inMs) : null;
  const explicitOut = Number.isFinite(Number(outMs)) && Number(outMs) > 0 ? Number(outMs) : null;
  const endMs = explicitOut != null ? explicitOut : treatOpenCheckoutAsNow ? nowMs : null;
  if (startMs != null && endMs != null) {
    diffMin = Math.max(0, endMs - startMs) / 60000;
  } else {
    let inMin = parseTimeToMinutes(checkIn);
    let outMin = parseTimeToMinutes(checkOut);
    if (inMin == null) return 0;
    if (outMin == null) {
      if (!treatOpenCheckoutAsNow) return 0;
      const now = new Date(nowMs);
      outMin = now.getHours() * 60 + now.getMinutes() + now.getSeconds() / 60;
    }
    diffMin = outMin - inMin;
    if (diffMin < 0) diffMin += 24 * 60;
  }

  const breakMin = deductBreak ? resolveBreakMinutesForShift(config, shift) : 0;
  const worked = Math.max(0, diffMin - breakMin) / 60;
  // Live / open-session hours stay exact; rounding is for closed-day payroll net only.
  if (treatOpenCheckoutAsNow) {
    return Math.round(worked * 10000) / 10000;
  }
  const unit = Number(config.workingHoursRoundingUnit) || 0;
  if (unit > 0) {
    const dir = config.workingHoursRoundingDirection || "nearest";
    const step = unit / 60;
    if (dir === "up") return Math.ceil(worked / step) * step;
    if (dir === "down") return Math.floor(worked / step) * step;
    return Math.round(worked / step) * step;
  }
  return Math.round(worked * 100) / 100;
}

/**
 * Shift break wins when the shift defines break (including 0).
 * Policy fixed break is only a fallback — otherwise clearing Flexi break has no effect
 * while policy still has breakFixedMinutes: 60.
 */
function resolveBreakMinutesForShift(config, shift) {
  if (!shift) {
    return Math.max(0, Number(config.breakFixedMinutes) || 0);
  }
  const s = normalizeShiftRecord(shift) || shift;
  if (s.breakHandling === "none") return 0;

  const hasShiftBreakField =
    s.unpaidBreakMinutes != null ||
    s.breakMinutes != null ||
    s.totalBreakMinutes != null ||
    s.breakHandling != null;

  if (hasShiftBreakField || config.breakDeductionMethod === "shift") {
    const unpaid = Number(
      s.unpaidBreakMinutes ?? s.breakMinutes ?? s.totalBreakMinutes ?? 0
    );
    return Math.max(0, Number.isFinite(unpaid) ? unpaid : 0);
  }

  return Math.max(0, Number(config.breakFixedMinutes) || 0);
}

/** Format net worked hours for day details (aligned with status engine). */
export function formatNetWorkedHoursLabel(workedHours) {
  if (!Number.isFinite(workedHours) || workedHours <= 0) return "--";
  const totalSec = Math.round(workedHours * 3600);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function normalizeStatusCode(status) {
  if (!status || typeof status !== "string") return "";
  const s = status.trim().toUpperCase();
  if (s === "-" || s === "--") return "";
  if (s === "LWP" || s === "LP" || s === "LO") return "LOP";
  return s.split("/")[0].trim();
}

function isLopLikeCode(code) {
  return code === "LOP" || code === "LWP" || code === "LP" || code === "LO";
}

function isLateHalfDayPenalty(timing) {
  if (!timing?.late || timing.graceConsumed) return false;
  return String(timing.penaltyType || "").toLowerCase() === "half_day";
}

function isLateFullDayPenalty(timing) {
  if (!timing) return false;
  const t = String(timing.penaltyType || "").toLowerCase();
  return (
    Boolean(timing.forceLop) &&
    (t === "full_day" || t === "absent" || t === "lop")
  );
}

export function isStructuralAttendanceStatus(status) {
  if (status == null) return false;
  const raw = String(status).trim();
  if (!raw || raw === "-") return false;

  // Half-day cells: structural if any half is leave / WO / PH / OD (not pure PR/LOP/-).
  // Previously any "/" returned false, so CL/PR was wiped to "-" by the punch engine.
  const parts = raw.includes("/")
    ? raw.split("/").map((p) => String(p || "").trim()).filter((p) => p !== "")
    : [raw];

  return parts.some((part) => {
    const code = normalizeStatusCode(part);
    if (!code || code === "-" || code === "PR" || isLopLikeCode(code)) return false;
    if (STRUCTURAL_STATUS_CODES.has(code)) return true;
    // Leave types and other custom codes (CL, EL, SL, …) — not LOP aliases (LP).
    if (code.length >= 2 && code !== "PR" && !isLopLikeCode(code)) return true;
    return false;
  });
}

function missingPunchStatus(policy, shift) {
  const config = getEffectiveTimingConfig(policy, shift);
  return missingPunchMarkToStatus(config.defaultMarkingForMissingPunch);
}

/**
 * Policy "Day marking (hours)" bands:
 *   < absent-if-below → absent (LOP)
 *   ≥ absent-if-below and < full-day-if-≥ → half day
 *   ≥ full-day-if-≥ → full day
 * "Half-day if hours below" at/above the full-day line is a ceiling (e.g. both 9).
 * Below the full-day line it stays the legacy floor (half day when hours ≥ that value).
 */
function resolveWorkedHoursBand(workedHours, config) {
  const hours = Number(workedHours) || 0;
  const fullAtLeast = Number(config.fullDayIfHoursAtLeast ?? config.fullDayHours ?? 8);
  const absentBelow = Number(config.absentMarkIfHoursBelow ?? 2);
  const halfField = Number(
    config.halfDayMarkIfHoursBelow ??
      config.halfDayIfHoursLessThan ??
      config.halfDayHours ??
      4
  );
  const autoHalf = config.autoHalfDayMarkingEnabled !== false;
  const autoAbsent = config.autoAbsentMarkingEnabled !== false;

  if (hours >= fullAtLeast) return "full";
  if (autoAbsent && hours < absentBelow) return "absent";
  if (autoHalf) {
    const usesCeiling = halfField >= fullAtLeast;
    const inHalf = usesCeiling
      ? hours >= absentBelow && hours < fullAtLeast
      : hours >= halfField && hours < fullAtLeast;
    if (inHalf) return "half";
  }
  return "absent";
}

function statusForHoursBand(band, timing) {
  if (band === "full") {
    if (isLateFullDayPenalty(timing)) return "LOP";
    if (isLateHalfDayPenalty(timing)) return HALF_DAY_LOP_PR;
    return "PR";
  }
  if (band === "half") {
    return isLateHalfDayPenalty(timing) ? HALF_DAY_LOP_PR : HALF_DAY_PR_LOP;
  }
  return "LOP";
}

function hoursBasedStatus(workedHours, policy, shift) {
  const config = getEffectiveTimingConfig(policy, shift);
  return statusForHoursBand(resolveWorkedHoursBand(workedHours, config), null);
}

/**
 * Live status while the session is still open (checked in, no check-out yet).
 * Below the absent line: provisional "-". At half-day or full-day hours: same
 * closed marks (PR, PR/LOP, LOP/PR, LOP) — never PR/- or LOP/-.
 */
export function progressiveOpenSessionStatus(workedHours, policy, shift, timing = null) {
  const config = policy
    ? getEffectiveTimingConfig(policy, shift)
    : {
        fullDayIfHoursAtLeast: 8,
        halfDayMarkIfHoursBelow: 4,
        absentMarkIfHoursBelow: 2,
        autoHalfDayMarkingEnabled: true,
        autoAbsentMarkingEnabled: true,
      };
  const band = resolveWorkedHoursBand(workedHours, config);
  if (band === "full" || band === "half") return statusForHoursBand(band, timing);
  return "-";
}

export function dayHasPunchData(checkIn, checkOut, worklogRow = null) {
  if (worklogRow?.inMs) return true;
  const hasIn = checkIn != null && checkIn !== "" && checkIn !== "--";
  const hasOut = checkOut != null && checkOut !== "" && checkOut !== "--";
  return hasIn || hasOut;
}

/**
 * Pre-check-in validation (navbar, biometric, API).
 * @param {"web"|"biometric"|"mobile"} channel — only that policy flag is enforced.
 */
export function evaluateCheckInPunch({
  employee,
  checkInMs = Date.now(),
  policies = null,
  shifts = null,
  shiftNameHint = null,
  channel = "web",
  /** Optional day worklog — avoids circular import with punch storage. */
  existingWorklog = null,
}) {
  const policyList = policies ?? loadAttendancePolicies();
  const shiftList = shifts ?? loadAttendanceShifts();
  const date = new Date(checkInMs);
  const checkInClock = date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const empId = employee?.id ?? employee?.employeeId;

  const policy = resolvePolicyForEmployee(employee, policyList, date);
  const shift = resolveShiftForEmployeeDay(
    employee,
    policy,
    shiftList,
    date,
    shiftNameHint
  );

  const warnings = [];
  let allowed = true;

  const ch = String(channel || "web").toLowerCase();
  if (policy) {
    if (ch === "web" && policy.checkInWeb === false) {
      allowed = false;
      warnings.push("Web check-in is disabled for your attendance policy.");
    } else if (ch === "biometric" && policy.checkInBiometric === false) {
      allowed = false;
      warnings.push("Biometric check-in is disabled for your attendance policy.");
    } else if (ch === "mobile" && policy.checkInMobile === false) {
      allowed = false;
      warnings.push("Mobile check-in is disabled for your attendance policy.");
    }
  }

  const existing = existingWorklog;
  const punchMode = String(policy?.punchMode || "double").toLowerCase();
  const hasCompletedPair = Boolean(
    existing?.inMs && existing.outMs != null && !existing.open
  );
  // Double / single: one pair per day. Multiple: allow further IN after OUT.
  if (
    hasCompletedPair &&
    (punchMode === "double" || punchMode === "single")
  ) {
    allowed = false;
    warnings.push(
      punchMode === "single"
        ? "Your attendance policy allows only a single punch per day."
        : "Your attendance policy allows only one check-in / check-out pair per day."
    );
  }

  // Late / early rules use First IN of the day — only warn on the first check-in.
  const isFirstInOfDay = !existing?.inMs;
  const timing = evaluateTimingRules(policy, shift, checkInClock, null, {
    employeeId: empId,
    date,
    consumeGrace: false,
  });

  if (isFirstInOfDay) {
    if (timing.graceConsumed) {
      warnings.push(
        `Late check-in within grace allowance (${timing.lateMinutes} min after shift start).`
      );
    } else if (timing.late) {
      if (timing.shortLeaveSuggested) {
        warnings.push(
          `Late by ${timing.lateMinutes} min — consider short leave / regularization.`
        );
      }
      if (timing.requiresShiftOverride) {
        warnings.push(
          "You are late beyond grace. Change today's shift (Attendance → Codes and devices / shift override) before check-in, or half-day LOP may apply."
        );
      }
      if (timing.penaltyType === "half_day") {
        warnings.push(
          "Late arrival: first half may show as LOP until full-day hours are completed."
        );
      } else if (timing.penaltyType === "full_day") {
        warnings.push("Policy may mark this day as full-day LOP due to late arrival.");
      }
    }
  }

  return {
    allowed,
    policy,
    shift,
    timing,
    warnings,
    checkInMs,
    channel: ch,
    requiresShiftOverride: isFirstInOfDay ? timing.requiresShiftOverride : false,
    shortLeaveSuggested: isFirstInOfDay ? timing.shortLeaveSuggested : false,
    projectedPenaltyStatus: isFirstInOfDay ? timing.penaltyStatus : null,
    punchMode,
    isFirstInOfDay,
  };
}

/**
 * Evaluate a single calendar day.
 */
export function evaluateDayAttendance({
  employee,
  date,
  storedStatus = "-",
  checkIn = null,
  checkOut = null,
  policies = null,
  shifts = null,
  shiftNameHint = null,
  isToday = false,
  openSession = false,
  /** Optional: session-sum hours (multi punch). When set, skips First→Last wall span. */
  workedHoursOverride = null,
}) {
  const policyList = policies ?? loadAttendancePolicies();
  const shiftList = shifts ?? loadAttendanceShifts();
  const d = date instanceof Date ? date : new Date(date);
  const empId = employee?.id ?? employee?.employeeId;

  if (isStructuralAttendanceStatus(storedStatus)) {
    return {
      status: null,
      workedHours: 0,
      policyId: null,
      shiftName: shiftNameHint,
      skipped: "structural",
    };
  }

  const policy = resolvePolicyForEmployee(employee, policyList, d);
  const shift = resolveShiftForEmployeeDay(employee, policy, shiftList, d, shiftNameHint);

  const hasIn = checkIn != null && checkIn !== "" && checkIn !== "--";
  const hasOut = checkOut != null && checkOut !== "" && checkOut !== "--";

  const punchMs = {
    inMs: Number.isFinite(Number(checkIn)) && Number(checkIn) > 1e11 ? Number(checkIn) : null,
    outMs: Number.isFinite(Number(checkOut)) && Number(checkOut) > 1e11 ? Number(checkOut) : null,
  };

  const resolveWorkedHours = (treatOpen, policyForHours = policy) => {
    if (
      workedHoursOverride != null &&
      Number.isFinite(Number(workedHoursOverride))
    ) {
      return Math.max(0, Number(workedHoursOverride));
    }
    return computeWorkedHours(checkIn, checkOut, policyForHours || {}, shift, {
      treatOpenCheckoutAsNow: treatOpen,
      // Day marks use clock hours (same as backend computeDayAttendanceStatus).
      // Break is not subtracted here — a 09:00–18:00 General day is 9h = full PR.
      deductBreak: false,
      ...punchMs,
    });
  };

  if (!hasIn && !hasOut) {
    // Manual process: do not force LOP — grid/calendar mark Present / Leave.
    if (resolveEmployeeAttendanceProcess(employee, d, policyList) === "manual") {
      return {
        status: null,
        workedHours: 0,
        policyId: policy?.id ?? null,
        shiftName: shift?.name ?? shiftNameHint,
        skipped: "manual_process",
        reasons: ["manual_process"],
      };
    }
    // Unmarked working day with no punch → LOP (leave / WO / PH handled as structural above).
    return {
      status: "LOP",
      workedHours: 0,
      policyId: policy?.id ?? null,
      shiftName: shift?.name ?? shiftNameHint,
      skipped: "no_punch",
      reasons: ["no_punch"],
    };
  }

  if (!policy) {
    if (isToday && openSession) {
      const worked = resolveWorkedHours(true, {});
      return {
        status: progressiveOpenSessionStatus(worked, null, shift),
        workedHours: worked,
        policyId: null,
        shiftName: shift?.name ?? null,
        reasons: ["open_session"],
      };
    }
    const worked = resolveWorkedHours(false, {});
    return {
      status: worked >= 8 ? "PR" : worked >= 4 ? HALF_DAY_PR_LOP : "LOP",
      workedHours: worked,
      policyId: null,
      shiftName: shift?.name ?? null,
    };
  }

  if (isToday && openSession) {
    const workedHours = resolveWorkedHours(true, policy);
    const timing = evaluateTimingRules(policy, shift, checkIn, null, {
      employeeId: empId,
      date: d,
      consumeGrace: false,
    });
    return {
      status: progressiveOpenSessionStatus(workedHours, policy, shift, timing),
      workedHours,
      policyId: policy.id,
      shiftName: shift?.name ?? null,
      reasons: ["open_session"],
      timing,
    };
  }

  if (hasIn && !hasOut) {
    return {
      status: missingPunchStatus(policy, shift),
      workedHours: 0,
      policyId: policy.id,
      shiftName: shift?.name ?? null,
      skipped: "missing_checkout",
      reasons: ["missing_checkout"],
    };
  }

  const workedHours = resolveWorkedHours(false, policy);

  const timing = evaluateTimingRules(policy, shift, checkIn, checkOut, {
    employeeId: empId,
    date: d,
    // Display / sync only — do not burn grace on every status tick.
    consumeGrace: false,
  });

  const config = getEffectiveTimingConfig(policy, shift);
  const band = resolveWorkedHoursBand(workedHours, config);

  if (band === "full") {
    if (isLateFullDayPenalty(timing)) {
      return {
        status: "LOP",
        workedHours,
        policyId: policy.id,
        shiftName: shift?.name ?? null,
        reasons: timing.reasons || ["timing_full_day_penalty"],
        timing,
      };
    }
    if (isLateHalfDayPenalty(timing)) {
      return {
        status: HALF_DAY_LOP_PR,
        workedHours,
        policyId: policy.id,
        shiftName: shift?.name ?? null,
        reasons: ["full_day_hours", "late_first_half"],
        timing,
      };
    }
    return {
      status: "PR",
      workedHours,
      policyId: policy.id,
      shiftName: shift?.name ?? null,
      reasons: [
        "full_day_hours",
        ...(timing.late ? ["late_waived_by_hours"] : []),
        ...(timing.graceConsumed ? ["grace_used"] : []),
      ],
      timing,
    };
  }

  if (band === "half") {
    const lateHalf = isLateHalfDayPenalty(timing);
    return {
      status: lateHalf ? HALF_DAY_LOP_PR : HALF_DAY_PR_LOP,
      workedHours,
      policyId: policy.id,
      shiftName: shift?.name ?? null,
      reasons: [
        "half_day_hours",
        ...(lateHalf ? ["late_first_half"] : []),
        ...(timing.late ? ["late"] : []),
        ...(timing.earlyExit ? ["early_exit"] : []),
      ],
      timing,
    };
  }

  if (timing.penaltyStatus) {
    const lateHalfBelow = isLateHalfDayPenalty(timing) && band === "absent";
    return {
      status: lateHalfBelow ? "LOP" : timing.penaltyStatus,
      workedHours,
      policyId: policy.id,
      shiftName: shift?.name ?? null,
      reasons: timing.reasons || ["timing_penalty"],
      timing,
    };
  }

  if (timing.forceLop) {
    return {
      status: "LOP",
      workedHours,
      policyId: policy.id,
      shiftName: shift?.name ?? null,
      reasons: timing.reasons || ["timing_violation"],
      timing,
    };
  }

  const status = hoursBasedStatus(workedHours, policy, shift);
  return {
    status,
    workedHours,
    policyId: policy.id,
    shiftName: shift?.name ?? null,
    reasons: [
      ...(timing.late ? ["late"] : []),
      ...(timing.earlyExit ? ["early_exit"] : []),
      ...(timing.graceConsumed ? ["grace_used"] : []),
      ...(timing.shortLeaveSuggested ? ["short_leave_window"] : []),
    ],
    timing,
  };
}

export function mergeDayDetailWithWorklog(dayDetail = {}, worklogRow = null) {
  if (!worklogRow) return dayDetail;
  const formatClock = (ms) =>
    new Date(ms).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const checkIn = formatClock(worklogRow.inMs);
  const checkOut =
    worklogRow.outMs != null ? formatClock(worklogRow.outMs) : dayDetail.checkOut ?? "--";
  let totalHours = dayDetail.totalHours ?? "--";
  if (worklogRow.outMs != null) {
    const diff = Math.max(0, worklogRow.outMs - worklogRow.inMs);
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    totalHours = `${h}h ${String(m).padStart(2, "0")}m`;
  } else if (worklogRow.open && worklogRow.inMs) {
    const diff = Math.max(0, Date.now() - worklogRow.inMs);
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    totalHours = `${h}h ${String(m).padStart(2, "0")}m`;
  }
  const sessionCount = Array.isArray(worklogRow.sessions)
    ? worklogRow.sessions.length
    : 1;
  return {
    ...dayDetail,
    checkIn,
    checkOut,
    totalHours,
    shiftAssigned: dayDetail.shiftAssigned || dayDetail.shiftName,
    punchSessions: sessionCount,
  };
}

export function buildMonthPolicyStatusMap({
  employee,
  year,
  month,
  statusMap = {},
  detailMap = {},
  worklogByDay = {},
  daysInMonth,
  now = new Date(),
  loggedInEmployeeId = null,
  autoApplyForAllEmployees = false,
  /** Optional: (date, employee) => status string — WO/PH/PR from Attendance Settings */
  getDayDefault = null,
  /** Optional explicit policy list (avoids stale/empty localStorage). */
  policies: policiesInput = null,
}) {
  const policies = Array.isArray(policiesInput)
    ? policiesInput
    : loadAttendancePolicies();
  const shifts = loadAttendanceShifts();
  const dim = daysInMonth ?? new Date(year, month + 1, 0).getDate();
  const result = {};
  const todayNum =
    now.getFullYear() * 10000 + (now.getMonth() + 1) * 100 + now.getDate();
  const empId = String(employee?.id ?? "");
  const isSelf =
    loggedInEmployeeId != null && empId !== "" && String(loggedInEmployeeId) === empId;
  // Without policies, keep stored statuses — do not invent LOP (would diverge from payroll).
  const employeeProcess =
    policies.length === 0
      ? "manual"
      : resolveEmployeeAttendanceProcess(employee, now, policies);

  for (let day = 1; day <= dim; day++) {
    const date = new Date(year, month, day);
    const dayKey = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    const cellNum = year * 10000 + (month + 1) * 100 + day;
    const stored = statusMap[String(day)] ?? statusMap[day] ?? "-";
    const detail = detailMap[String(day)] || detailMap[day] || {};
    const worklog = worklogByDay[dayKey] || null;
    const merged = mergeDayDetailWithWorklog(detail, worklog);
    const override = getShiftOverrideForDay(employee?.id, dayKey);
    if (override?.shiftName) {
      merged.shiftAssigned = override.shiftName;
    } else {
      const approvedName = resolveApprovedChangeShiftName(employee?.id, dayKey);
      if (approvedName) merged.shiftAssigned = approvedName;
    }

    const sandwichApplied = !!(
      detail.sandwichApplied === true ||
      detail.sandwich_applied === true ||
      detail.sandwichApplied === 1 ||
      detail.sandwich_applied === 1
    );
    const storedHeadEarly = String(stored || "")
      .toUpperCase()
      .split("/")[0];
    const changeWoWorking = hasApprovedChangeWoForDay(employee?.id, dayKey);
    // Sandwich-converted weekly off is LP in DB and on the cell (not WO overlay).
    if (sandwichApplied && cellNum <= todayNum && !changeWoWorking) {
      result[String(day)] = isLopLikeCode(storedHeadEarly) ? stored : "LP";
      continue;
    }

    if (isStructuralAttendanceStatus(stored)) {
      const storedHead = String(stored || "")
        .toUpperCase()
        .split("/")[0];
      const proposedWo = hasApprovedProposedWoForDay(employee?.id, dayKey);
      if (changeWoWorking && (storedHead === "WO" || storedHead === "CW")) {
        // fall through to working-day paint
      } else if (
        (storedHead === "WO" || storedHead === "CW") &&
        !proposedWo &&
        typeof getDayDefault === "function"
      ) {
        const calDef = String(getDayDefault(date, employee) || "")
          .toUpperCase()
          .split("/")[0];
        if (calDef !== "WO" && calDef !== "PH" && calDef !== "HO") {
          // Ghost stored WO after weekday was removed from Weekly Off policy.
        } else if (storedHead === "CW" && (calDef === "WO" || calDef === "PH" || calDef === "HO")) {
          result[String(day)] = calDef === "HO" ? "PH" : calDef;
          continue;
        } else {
          result[String(day)] = stored;
          continue;
        }
      } else {
        // Legacy CW on a calendar off day: paint WO/PH (CW not required on monthly cell)
        if (storedHead === "CW" && typeof getDayDefault === "function") {
          const calDef = String(getDayDefault(date, employee) || "")
            .toUpperCase()
            .split("/")[0];
          if (calDef === "WO" || calDef === "PH" || calDef === "HO") {
            result[String(day)] = calDef === "HO" ? "PH" : calDef;
            continue;
          }
        }
        // Keep WO / PH / leave / RH / OD etc. even on future days (approved leave/RH).
        result[String(day)] = stored;
        continue;
      }
    }

    // LOCKED C5: calendar WO/PH must paint over stored LP/PR/CW (not leave/CO/OD/RH).
    // Approved Change WO current date is no longer calendar WO — do not overlay Sunday WO.
    if (!changeWoWorking && typeof getDayDefault === "function") {
      const structuralDefault = getDayDefault(date, employee);
      const defCode = String(structuralDefault || "")
        .toUpperCase()
        .split("/")[0];
      if (defCode === "WO" || defCode === "PH" || defCode === "HO") {
        const storedHead = String(stored || "")
          .toUpperCase()
          .split("/")[0];
        const preserveOverCalendar = new Set([
          "CO",
          "OD",
          "WFH",
          "RH",
          "CL",
          "EL",
          "SL",
          "ML",
          "PL",
        ]);
        if (preserveOverCalendar.has(storedHead)) {
          result[String(day)] = stored;
          continue;
        }
        if (
          storedHead &&
          storedHead !== "-" &&
          storedHead !== "PR" &&
          storedHead !== "CW" &&
          !isLopLikeCode(storedHead) &&
          storedHead.length >= 2
        ) {
          // Other configured leave-type codes on the row
          result[String(day)] = stored;
          continue;
        }
        result[String(day)] = defCode === "HO" ? "PH" : defCode;
        continue;
      }
    }

    // Admin Change Status override (audited) — sticky on *working* days until cleared.
    // Must run after locked C5 WO/PH paint so calendar off-days still win in DB+UI.
    // On working days this blocks auto past-day LOP paint from wiping the override.
    const isManualStatusOverride = !!(
      detail.isStatusOverride === true ||
      detail.is_status_override === true ||
      detail.isStatusOverride === 1 ||
      detail.is_status_override === 1
    );
    if (isManualStatusOverride && stored && stored !== "-") {
      result[String(day)] = stored;
      continue;
    }

    // Auto process: blank future working days until the day arrives / engine fills them.
    // Must run AFTER structural check so approved CL/RH on future dates still show.
    if (cellNum > todayNum && employeeProcess !== "manual") {
      result[String(day)] = "-";
      continue;
    }

    // Manual Override: empty days use structural defaults (WO/PH/PR), never punch engine.
    // Empty policy cache used to freeze stored LP and skip live punch status — still
    // evaluate today / punch days so ESS Check In/Out can paint live hours marks.
    if (employeeProcess === "manual" && policies.length > 0) {
      const fallback =
        typeof getDayDefault === "function"
          ? getDayDefault(date, employee) || getWorkingDayDefaultForEmployee(employee, date)
          : getWorkingDayDefaultForEmployee(employee, date);
      result[String(day)] =
        stored && stored !== "-" ? stored : fallback || "PR";
      continue;
    }

    // Auto path: still honour WO/PH from Settings when day is empty.
    if (
      (!stored || stored === "-") &&
      typeof getDayDefault === "function"
    ) {
      const structural = getDayDefault(date, employee);
      const code = String(structural || "").toUpperCase();
      if (
        code === "WO" ||
        code === "PH" ||
        code === "WOPR" ||
        code === "PRWO" ||
        code === "PHPR" ||
        code === "PRPH"
      ) {
        result[String(day)] = structural;
        continue;
      }
    }

    const isToday =
      year === now.getFullYear() && month === now.getMonth() && day === now.getDate();
    const hasPunch = dayHasPunchData(merged.checkIn, merged.checkOut, worklog);
    const hasInClock =
      (merged.checkIn != null && merged.checkIn !== "" && merged.checkIn !== "--") ||
      Boolean(worklog?.inMs);
    const hasOutClock =
      (worklog != null
        ? worklog.open !== true && worklog.outMs != null
        : merged.checkOut != null &&
          merged.checkOut !== "" &&
          merged.checkOut !== "--");
    const openSession =
      isToday &&
      Boolean(worklog?.open || (hasInClock && !hasOutClock && worklog?.outMs == null));

    const shouldEvaluate =
      autoApplyForAllEmployees ||
      hasPunch ||
      (isSelf && (isToday || hasPunch || cellNum <= todayNum));

    if (!shouldEvaluate) {
      result[String(day)] = stored && stored !== "-" ? stored : "-";
      continue;
    }

    // Today before any punch: provisional blank (not LOP yet).
    if (isToday && !hasPunch) {
      result[String(day)] = "-";
      continue;
    }

    // Past working day with no punch → LOP only when the cell is still unset.
    // Stored PR/OD/leave/WO from Process/DB must not be painted LOP just because
    // a lightweight history payload omitted check-in/out (Process totals flash then LOP).
    if (!hasPunch && !isToday) {
      if (stored && stored !== "-") {
        result[String(day)] = stored;
        continue;
      }
      result[String(day)] = "LOP";
      continue;
    }

    // Closed days: server status is authoritative for full-day marks.
    // Re-evaluate half-day PR/LOP / LOP/PR from in/out + assigned shift so a
    // stale Regularise/import half mark (e.g. 9h still PR/LOP) can correct.
    const storedUpper = String(stored || "").toUpperCase();
    const isHalfDayMark = storedUpper.includes("/");
    if (!openSession && hasOutClock && stored && stored !== "-" && !isHalfDayMark) {
      result[String(day)] = stored;
      continue;
    }

    const workedHoursOverride =
      worklog?.inMs != null
        ? workedMsFromSessions(
            { ...worklog, open: Boolean(openSession) },
            now.getTime()
          ) / 3600000
        : null;

    const evalResult = evaluateDayAttendance({
      employee,
      date,
      storedStatus: stored,
      checkIn: merged.checkIn,
      checkOut: openSession ? null : merged.checkOut,
      policies,
      shifts,
      shiftNameHint: merged.shiftAssigned || merged.shiftName,
      isToday,
      openSession,
      workedHoursOverride,
    });

    if (evalResult.status) {
      result[String(day)] = evalResult.status;
    } else if (stored && stored !== "-") {
      result[String(day)] = stored;
    } else {
      result[String(day)] = "-";
    }
  }

  return result;
}

export function recalculateDayAttendance(input) {
  return evaluateDayAttendance(input);
}
